import {withHandlers} from 'recompose'

import compose from 'lodash/flowRight'
import memoize from 'memoize-one'
import * as React from 'react'
import {connect} from 'react-redux'

import {
  hideQueuedBuildsForProject,
  showQueuedBuildsForProject,
  setShowQueuedBuildsCount as setShowQueuedBuildsCountAction,
} from '../../../../../../actions'
import type {State} from '../../../../../../reducers/types'
import {getStatusKey} from '../../../../../../rest/schemata'
import {
  getShowQueuedBuildsCount,
  getAutoExpandQueuedBuilds,
  getShowQueuedBuildsInProject,
} from '../../../../../../selectors'
import {getBuildTypeStatusRequest} from '../../../../../../types'
import type {BuildId, NormalizedBuild} from '../../../../../../types'
import stableSort from '../../../../../../utils/stableSort'
import BuildsWrapped, {BuildsWrappedPlaceholder} from '../../../../../common/Builds/Builds.wrapped'

import type {
  BuildsListActionProps,
  BuildsListComposeProps,
  BuildsListOwnProps,
  BuildsListStateProps,
} from './BuildTypeLine.types'
import {sortByFinishDate} from './BuildTypeLine.utils'

type Props = BuildsListStateProps &
  BuildsListActionProps &
  BuildsListOwnProps &
  BuildsListComposeProps

type ExtractedBuilds = {
  queuedBuildIds: BuildId[]
  nonQueuedBuildIds: BuildId[]
}

const extractBuilds = (builds: ReadonlyArray<NormalizedBuild>): ExtractedBuilds =>
  stableSort(builds, sortByFinishDate).reduce<ExtractedBuilds>(
    (accumulator, build) => {
      if (build.state === 'queued') {
        accumulator.queuedBuildIds.push(build.id)
      } else {
        accumulator.nonQueuedBuildIds.push(build.id)
      }

      return accumulator
    },
    {
      queuedBuildIds: [],
      nonQueuedBuildIds: [],
    },
  )

const mapStateToProps: () => (
  arg0: State,
  arg1: BuildsListOwnProps,
) => BuildsListStateProps = () => {
  const memoizedExtractBuilds = memoize(extractBuilds)
  return (state, {buildTypeId, builds, branch}) => {
    const statusKey = getStatusKey(getBuildTypeStatusRequest(buildTypeId, branch))
    const queuedVisible =
      getShowQueuedBuildsInProject(state, buildTypeId) ||
      getAutoExpandQueuedBuilds(state, statusKey)
    const showQueuedBuildsCount = getShowQueuedBuildsCount(state, buildTypeId)
    const {queuedBuildIds, nonQueuedBuildIds} = memoizedExtractBuilds(builds)
    return {
      queuedVisible,
      showQueuedBuildsCount,
      queuedBuildIds,
      nonQueuedBuildIds,
    }
  }
}

const actionCreators: BuildsListActionProps = {
  showQueuedBuildsForProject,
  hideQueuedBuildsForProject,
  setShowQueuedBuildsCount: setShowQueuedBuildsCountAction,
}

function BuildTypeLineBuilds({
  queuedVisible,
  toggleQueued,
  ready,
  queuedBuildIds,
  nonQueuedBuildIds,
  buildTypeId,
  showQueuedBuildsCount,
  setShowQueuedBuildsCount,
  nested,
}: Props) {
  const onlyQueuedPresented = queuedBuildIds.length > 0 && nonQueuedBuildIds.length === 0

  if (!ready) {
    return (
      <BuildsWrappedPlaceholder
        hasParentGrid
        onlyQueuedPresented={onlyQueuedPresented}
        queuedCountToRender={queuedBuildIds.length}
        queuedVisible={queuedVisible}
      />
    )
  }

  return (
    <BuildsWrapped
      hasParentGrid
      buildIds={nonQueuedBuildIds}
      queuedBuildIds={queuedBuildIds}
      queuedVisible={queuedVisible}
      onToggle={toggleQueued}
      buildTypeId={buildTypeId}
      queuedBuildsCountToShow={showQueuedBuildsCount}
      setShowQueuedBuildsCount={setShowQueuedBuildsCount}
      nested={nested}
    />
  )
}

export default compose(
  connect(mapStateToProps, actionCreators),
  React.memo,
  withHandlers({
    toggleQueued:
      ({
        buildTypeId,
        queuedVisible,
        branch,
        ...props
      }: BuildsListStateProps & BuildsListActionProps & BuildsListOwnProps) =>
      () =>
        queuedVisible
          ? props.hideQueuedBuildsForProject(buildTypeId, branch)
          : props.showQueuedBuildsForProject(buildTypeId),
  }),
)(BuildTypeLineBuilds)
